import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as Starknet from 'starknet';
import BigNumber from 'bignumber.js';

import createSafeContext from '#/utils/createSafeContext';
import { EthereumAddress } from '#/features/ethereum-wallet/wallet';

import { SystemConfig, getSystemConfig } from '#/api/system';
import { useApi } from '#/api/fetch-api';

export interface SystemConfigState {
  readonly status: 'loading' | 'error' | 'done';
  readonly config: SystemConfig | null;
}

const INITIAL_STATE: SystemConfigState = {
  status: 'loading',
  config: null,
};

type SystemConfigStatusView = SystemConfigState['status'];

interface TokenView {
  readonly symbol: string;
  readonly name: string;
  readonly l1TokenAddress: EthereumAddress;
  readonly l1BridgeAddress: EthereumAddress;
  readonly l2TokenAddress: string;
  readonly l2BridgeAddress: string;
  readonly decimals: number;
}

interface SystemConfigView {
  readonly ethereum: {
    readonly chainId: number;
  };
  readonly starknet: {
    readonly chainId: string;
    readonly baseUrl: string;
    readonly explorerUrl: string;
    readonly accountClassHash: string;
    readonly accountProxyClassHash: string;
  };
  readonly paraclear: {
    readonly address: string;
    readonly decimals: number;
  };

  readonly getToken: (symbol: string) => TokenView;
}

export const [useSystemConfigStatusView, SystemConfigStatusViewProvider] =
  createSafeContext<SystemConfigStatusView>('SystemConfigStatusView');

export const [useSystemConfig, SystemConfigViewProvider] =
  createSafeContext<SystemConfigView>('SystemConfigView');

interface ProviderProps {
  readonly children: ReactNode;
}

export function SystemConfigProvider(props: ProviderProps) {
  const [state, setState] = useState(INITIAL_STATE);

  const statusview = useMemo(() => createStatusView(state), [state]);
  const view = useMemo(() => createView(state), [state]);

  useSystemConfigFetch(setState);

  return view == null ? (
    <SystemConfigStatusViewProvider value={statusview}>
      {props.children}
    </SystemConfigStatusViewProvider>
  ) : (
    <SystemConfigStatusViewProvider value={statusview}>
      <SystemConfigViewProvider value={view}>
        {props.children}
      </SystemConfigViewProvider>
    </SystemConfigStatusViewProvider>
  );
}

export function createStatusView(
  state: SystemConfigState,
): SystemConfigStatusView {
  return state.status;
}

type CreateViewProps = Pick<SystemConfigState, 'config'>;

export function createView({
  config,
}: CreateViewProps): SystemConfigView | null {
  if (config == null) return null;

  const tokens = config.bridged_tokens.reduce<Record<string, TokenView>>(
    (acc, token) => {
      acc[token.symbol] = tokenView(token);
      return acc;
    },
    {},
  );

  return {
    ethereum: {
      chainId: new BigNumber(config.l1_chain_id, 10).toNumber(),
    },

    starknet: {
      chainId: Starknet.shortString.encodeShortString(config.starknet_chain_id),
      baseUrl: config.starknet_gateway_url,
      explorerUrl: config.block_explorer_url,
      accountClassHash: config.paraclear_account_hash,
      accountProxyClassHash: config.paraclear_account_proxy_hash,
    },

    paraclear: {
      address: config.paraclear_address,
      decimals: config.paraclear_decimals,
    },

    getToken: (symbol: string) => {
      const token = tokens[symbol];
      if (token == null) {
        throw new Error(`Unknown token: ${symbol}`);
      }
      return token;
    },
  };
}

function tokenView(token: SystemConfig['bridged_tokens'][number]): TokenView {
  return {
    symbol: token.symbol,
    name: token.name,
    l1TokenAddress: token.l1_token_address,
    l1BridgeAddress: token.l1_bridge_address,
    l2TokenAddress: token.l2_token_address,
    l2BridgeAddress: token.l2_bridge_address,
    decimals: token.decimals,
  };
}

function useSystemConfigFetch(
  setState: Dispatch<SetStateAction<SystemConfigState>>,
): void {
  const [, exec] = useApi(getSystemConfig);

  useEffect(() => {
    setState((prev) => ({ ...prev, status: 'loading', error: '' }));
    exec({}, (result) => {
      if (result.ok) {
        setState((prev) => ({
          ...prev,
          status: 'done',
          config: result.data,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          status: 'error',
          config: null,
        }));
      }
    });
  }, [exec, setState]);
}
